import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import './estilodashuser.css';

const TIERS = {
  starter: { label: 'Starter', licencasPorProduto: 2 },
  trader: { label: 'Trader', licencasPorProduto: 5 },
  pro: { label: 'Pro', licencasPorProduto: 10 },
};

function getTipoAssinatura(assinatura) {
  return String(assinatura?.tipo || '').toLowerCase() === 'pacote'
    ? 'pacote'
    : 'individual';
}

function buildWhatsAppPlanLink({ nomeCliente, nomePlano, tier }) {
  const msg =
    `Olá! Gostaria de solicitar alteração do meu plano.\n\n` +
    `Cliente: ${nomeCliente || 'Cliente'}\n` +
    `Assinatura: ${nomePlano || 'Assinatura'}\n` +
    `Novo tier desejado: ${tier || '—'}\n\n` +
    `Pode me ajudar com a alteração?`;
  return `https://wa.me/5542984241307?text=${encodeURIComponent(msg)}`;
}

export default function Cliente({ userId }) {
  const [cliente, setCliente] = useState(null);
  const [assinaturas, setAssinaturas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [pacotes, setPacotes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [mensagem, setMensagem] = useState('');
  const [tipoMensagem, setTipoMensagem] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [alterandoPlanoId, setAlterandoPlanoId] = useState('');
  const [novosTiers, setNovosTiers] = useState({});

  useEffect(() => {
    if (userId) carregarDados();
  }, [userId]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [respCliente, respAss, respProd, respPac] = await Promise.all([
        api.get(`/api/clientes/${userId}`),
        api.get('/api/minhas-assinaturas'),
        api.get('/api/produtos-ativos'),
        api.get('/api/pacotes-ativos'),
      ]);

      setCliente(respCliente.data || null);
      setAssinaturas(respAss.data || []);
      setProdutos(respProd.data || []);
      setPacotes(respPac.data || []);
      setMensagem('');
      setTipoMensagem('');
    } catch (error) {
      console.error('Erro ao carregar informações do cliente:', error);
      setMensagem(
        'Erro ao carregar suas informações. Tente novamente em alguns instantes.'
      );
      setTipoMensagem('erro');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (campo, valor) => {
    setCliente((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleSalvar = async () => {
    if (!cliente) return;

    try {
      setSalvando(true);
      setMensagem('');
      setTipoMensagem('');

      await api.put(`/api/clientes/${cliente.id}`, {
        nome: cliente.nome,
        email: cliente.email,
        phone: cliente.phone,
        cpf: cliente.cpf,
        slots_contratados: cliente.slots_contratados,
        dia_vencimento: cliente.dia_vencimento,
      });

      setMensagem('Seus dados foram atualizados com sucesso.');
      setTipoMensagem('sucesso');
    } catch (error) {
      console.error('Erro ao atualizar dados do cliente:', error);
      const msg =
        error.response?.status === 403
          ? 'Seu servidor atual ainda não permite autoatualização completa dos dados do cliente. Fale com o suporte para liberar essa alteração.'
          : error.response?.data?.message ||
            'Erro ao atualizar seus dados. Tente novamente.';
      setMensagem(msg);
      setTipoMensagem('erro');
    } finally {
      setSalvando(false);
    }
  };

  const assinaturasAtivas = useMemo(() => {
    return assinaturas.filter((a) => Number(a.status) === 1);
  }, [assinaturas]);

  const resumoAssinaturas = useMemo(() => {
    return assinaturasAtivas.map((a) => {
      const tier = String(a.tier || 'starter');
      const tipo = getTipoAssinatura(a);
      const nomePlano = a.nome_plano || a.codigo_plano || 'Assinatura';
      let valor = 0;

      if (tipo === 'pacote') {
        const pacote = pacotes.find(
          (p) => String(p.codigo || '').toLowerCase() === String(a.codigo_plano || '').toLowerCase()
        );
        valor = Number(pacote?.precos?.[tier]?.valor_brl || 0);
      } else {
        const produto = produtos.find(
          (p) => String(p.codigo || '').toLowerCase() === String(a.codigo_plano || '').toLowerCase()
        );
        valor = Number(produto?.precos?.[tier]?.valor_brl || 0);
      }

      return {
        ...a,
        nomePlano,
        tipo,
        valor,
        tierInfo: TIERS[tier] || TIERS.starter,
      };
    });
  }, [assinaturasAtivas, produtos, pacotes]);

  const valorMensalEstimado = useMemo(() => {
    return resumoAssinaturas.reduce((acc, item) => acc + Number(item.valor || 0), 0);
  }, [resumoAssinaturas]);

  const handleTierTempChange = (assinaturaId, tier) => {
    setNovosTiers((prev) => ({
      ...prev,
      [assinaturaId]: tier,
    }));
  };

  const handleAlterarPlano = async (assinatura) => {
    const novoTier = novosTiers[assinatura.id];
    if (!novoTier || novoTier === assinatura.tier) return;

    try {
      setAlterandoPlanoId(String(assinatura.id));
      setMensagem('');
      setTipoMensagem('');

      const slots = (TIERS[novoTier] || TIERS.starter).licencasPorProduto;

      await api.put(`/api/assinaturas/${assinatura.id}`, {
        tier: novoTier,
        slots_contratados: slots,
      });

      setMensagem('Plano alterado com sucesso.');
      setTipoMensagem('sucesso');
      await carregarDados();
    } catch (error) {
      console.error('Erro ao alterar plano:', error);

      if (error.response?.status === 403) {
        setMensagem(
          'Seu servidor atual ainda não libera alteração de plano diretamente para o cliente. Use o botão de suporte abaixo para solicitar a troca.'
        );
      } else {
        setMensagem(
          error.response?.data?.message || 'Não foi possível alterar o plano agora.'
        );
      }

      setTipoMensagem('erro');
    } finally {
      setAlterandoPlanoId('');
    }
  };

  if (loading) {
    return <div className="loading">Carregando informações da sua conta...</div>;
  }

  if (!cliente) {
    return (
      <div className="error-message">
        Não foi possível carregar os dados da sua conta.
      </div>
    );
  }

  return (
    <div className="cliente-page">
      <div className="cliente-card cliente-card--wide">
        <div className="cliente-header">
          <div className="cliente-title">
            <span className="cliente-icon">👤</span>
            <div>
              <h3 className="cliente-h3">Minha Conta</h3>
              <div className="cliente-subtitle">
                Confira seus dados, veja o que está contratado e solicite alterações de plano quando necessário.
              </div>
            </div>
          </div>

          <div className="cliente-badges">
            {String(cliente.status) === '1' ? (
              <span className="badge badge-ativo">Conta ativa</span>
            ) : (
              <span className="badge badge-inativo">Conta inativa</span>
            )}

            {String(cliente.isento) === '1' ? (
              <span className="badge badge-isento">Isento</span>
            ) : (
              <span className="badge badge-normal">Cobrança normal</span>
            )}
          </div>
        </div>

        {mensagem && (
          <div
            className={
              tipoMensagem === 'erro'
                ? 'alert-box alert-error'
                : 'alert-box alert-success'
            }
          >
            {mensagem}
          </div>
        )}

        <div className="cliente-help-box">
          <strong>Como usar esta área:</strong> mantenha seu e-mail e telefone atualizados, confira suas assinaturas ativas
          e use os controles abaixo para revisar o tier de cada contratação. Se o seu servidor ainda não permitir troca direta
          de plano, a própria tela já vai orientar a solicitar a alteração ao suporte.
        </div>

        <div className="cliente-grid">
          <div className="cliente-form-group">
            <label>Nome</label>
            <input type="text" value={cliente.nome || ''} disabled />
          </div>

          <div className="cliente-form-group">
            <label>CPF</label>
            <input type="text" value={cliente.cpf || ''} disabled />
          </div>

          <div className="cliente-form-group">
            <label>E-mail</label>
            <input
              type="email"
              value={cliente.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="seuemail@exemplo.com"
            />
          </div>

          <div className="cliente-form-group">
            <label>Telefone</label>
            <input
              type="text"
              value={cliente.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(51) 99999-9999"
            />
          </div>

          <div className="cliente-form-group">
            <label>Dia de vencimento</label>
            <input type="text" value={cliente.dia_vencimento || '—'} disabled />
          </div>

          <div className="cliente-form-group">
            <label>Valor mensal estimado atual</label>
            <input
              type="text"
              value={`R$ ${valorMensalEstimado.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
              disabled
            />
          </div>
        </div>

        <div className="cliente-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={handleSalvar}
            disabled={salvando}
          >
            {salvando ? 'Salvando...' : 'Salvar meus dados'}
          </button>
        </div>
      </div>

      <div className="cliente-card cliente-card--wide" style={{ marginTop: 18 }}>
        <div className="cliente-header">
          <div className="cliente-title">
            <span className="cliente-icon">🧾</span>
            <div>
              <h3 className="cliente-h3">Minhas Assinaturas</h3>
              <div className="cliente-subtitle">
                Cada assinatura libera licenças por produto conforme o tier contratado.
              </div>
            </div>
          </div>
        </div>

        {resumoAssinaturas.length === 0 ? (
          <div className="cliente-empty-box">
            Nenhuma assinatura ativa foi encontrada. Se você já contratou um robô ou pacote, fale com o suporte para revisar sua conta.
          </div>
        ) : (
          <div className="cliente-subscriptions-grid">
            {resumoAssinaturas.map((assinatura) => {
              const tierAtual = String(assinatura.tier || 'starter');
              const novoTier = novosTiers[assinatura.id] || tierAtual;
              const whatsLink = buildWhatsAppPlanLink({
                nomeCliente: cliente.nome,
                nomePlano: assinatura.nomePlano,
                tier: novoTier,
              });

              return (
                <div key={assinatura.id} className="subscription-card">
                  <div className="subscription-card-top">
                    <div>
                      <div className="subscription-type">
                        {assinatura.tipo === 'pacote' ? 'Pacote' : 'Produto individual'}
                      </div>
                      <div className="subscription-name">{assinatura.nomePlano}</div>
                    </div>
                    <span className={Number(assinatura.status) === 1 ? 'badge badge-ativo' : 'badge badge-inativo'}>
                      {Number(assinatura.status) === 1 ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>

                  <div className="subscription-line">
                    <span>Tier atual</span>
                    <strong>{(TIERS[tierAtual] || TIERS.starter).label}</strong>
                  </div>

                  <div className="subscription-line">
                    <span>Licenças por produto</span>
                    <strong>{assinatura.slots_contratados}</strong>
                  </div>

                  <div className="subscription-line">
                    <span>Valor mensal estimado</span>
                    <strong>
                      R$ {Number(assinatura.valor || 0).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </div>

                  <div className="subscription-plan-change">
                    <label>Deseja mudar este plano?</label>
                    <select
                      value={novoTier}
                      onChange={(e) => handleTierTempChange(assinatura.id, e.target.value)}
                    >
                      {Object.entries(TIERS).map(([key, item]) => (
                        <option key={key} value={key}>
                          {item.label} ({item.licencasPorProduto} licenças por produto)
                        </option>
                      ))}
                    </select>

                    <div className="subscription-actions">
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={alterandoPlanoId === String(assinatura.id) || novoTier === tierAtual}
                        onClick={() => handleAlterarPlano(assinatura)}
                      >
                        {alterandoPlanoId === String(assinatura.id)
                          ? 'Alterando...'
                          : 'Alterar plano'}
                      </button>

                      <a
                        className="btn-outline-link"
                        href={whatsLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Solicitar no suporte
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
